import React, { useContext, useState } from 'react';
import { Box, Button, Typography, LinearProgress, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../UserAndProfile/UserContext';
import {
  getCurrentWeek,
  getTodaysMission,
  getWeeklyPaceMessage,
  getTaskRoute,
  isStudyPlanStale,
  getWeeklyGoalProgress,
} from '../StudyPlan/studyPlanUtils';
import { StudyPlanNudgeData, dismissNudgeForSession } from './chatUtils';

type StudyPlanNudgeProps = {
  /** Compact bar above the input */
  variant: 'mini-bar' | 'completion';
  nudge?: StudyPlanNudgeData | null;
  onDismissed?: () => void;
};

const StudyPlanNudge: React.FC<StudyPlanNudgeProps> = ({ variant, nudge, onDismissed }) => {
  const { user, updateStudyPlanTask } = useContext(UserContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);

  if (!user?.studyPlan || isStudyPlanStale(user.studyPlan)) return null;

  const currentWeek = getCurrentWeek(user.studyPlan, new Date());
  if (!currentWeek) return null;

  if (variant === 'mini-bar') {
    const mission = getTodaysMission(user);
    const progress = getWeeklyGoalProgress(currentWeek);
    const pace = getWeeklyPaceMessage(currentWeek);

    if (!mission && progress.target === 0) return null;

    return (
      <Box
        sx={{
          mb: 1.5, px: 2, py: 1.25,
          bgcolor: 'background.paper',
          border: '1px solid', borderColor: 'divider',
          borderRadius: 2,
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, cursor: mission ? 'pointer' : 'default' }}
          onClick={() => { if (mission?.route) navigate(mission.route); }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
              {mission?.title || 'This week\'s practice'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              {pace || mission?.subtitle || ''}
            </Typography>
          </Box>
          {progress.target > 0 && (
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600, flexShrink: 0, fontSize: '0.8rem' }}>
              {progress.current}/{progress.target}
            </Typography>
          )}
        </Box>
        {progress.target > 0 && (
          <LinearProgress
            variant="determinate"
            value={Math.min(100, progress.pct)}
            sx={{
              mt: 1, height: 6, borderRadius: 3, bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                bgcolor: progress.pct >= 100 ? 'custom.green' : 'primary.main',
              },
            }}
          />
        )}
      </Box>
    );
  }

  // completion variant
  if (!nudge || completed) return null;

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await updateStudyPlanTask(nudge.task.id, {
        isCompleted: true,
        completionDate: new Date().toISOString(),
      });
      setCompleted(true);
      dismissNudgeForSession();
      onDismissed?.();
    } catch (err) {
      console.error('Failed to complete study plan task from chat nudge:', err);
    } finally {
      setCompleting(false);
    }
  };

  const handleDismiss = () => {
    dismissNudgeForSession();
    setCompleted(true);
    onDismissed?.();
  };

  const handleOpenTask = () => {
    const route = getTaskRoute(nudge.task);
    if (route) navigate(route);
  };

  return (
    <Box
      sx={{
        mb: 2, px: 2, py: 1.5,
        bgcolor: 'custom.veryLightOrange',
        border: '2px solid', borderColor: 'primary.main',
        borderRadius: 2,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
        {nudge.title}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.primary', mb: 1.5, lineHeight: 1.4 }}>
        {nudge.body}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          size="small"
          disabled={completing}
          onClick={handleComplete}
          sx={{
            textTransform: 'none',
            bgcolor: theme.palette.custom.green,
            '&:hover': { bgcolor: theme.palette.custom.darkgreen },
          }}
        >
          {completing ? 'Saving…' : 'Mark complete'}
        </Button>
        <Button variant="outlined" size="small" onClick={handleDismiss} sx={{ textTransform: 'none' }}>
          Not now
        </Button>
        <Button variant="text" size="small" onClick={handleOpenTask} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Open task
        </Button>
      </Box>
    </Box>
  );
};

export default StudyPlanNudge;
